import './ui/styles.css';
import { icon as iconHelper } from './ui/icons';
import { AssistantPanel } from './ai/AssistantPanel';
import { HtmlAudioPlayer } from './audio/file';
import { YoutubePlayer } from './audio/youtube';
import {
  RecordingSession,
  captureScreenshot,
  downloadBlob,
} from './capture/recorder';
import { FontModal } from './fonts/FontModal';
import { ensureAllLoaded, initFonts } from './fonts/registry';
import { setFontModalOpener } from './inspector/Inspector';
import { installExternalAPI } from './integrations/external';
import { ProjectModal } from './projects/ProjectModal';
import { ScriptModal } from './scripts/ScriptModal';
import { BackgroundModal } from './assets/BackgroundModal';
import {
  bringForward,
  bringToFront,
  concatScripts,
  copyLayer,
  cutLayer,
  duplicateLayer,
  getState as getProjectState,
  initStore,
  pasteLayer,
  redo as projectRedo,
  removeLayer,
  removeLayers,
  sendBackward,
  sendToBack,
  setTool,
  subscribe as subscribeProject,
  undo as projectUndo,
  updateLayer as updateProjectLayer,
} from './store/project';
import { Canvas } from './canvas/Canvas';
import { Inspector } from './inspector/Inspector';
import { LayerPanel, enableSectionReorder } from './layers/LayerPanel';
import { Timeline } from './timeline/Timeline';
import { StatusBar } from './ui/StatusBar';
import { TopBar } from './ui/TopBar';
import {
  getState,
  setPlayhead,
  setPlaying,
  setRecordMode,
  subscribe,
  updateProject,
} from './store/project';

function buildShell(): {
  topbar: HTMLElement;
  statusbar: HTMLElement;
  layers: HTMLElement;
  stage: HTMLElement;
  inspector: HTMLElement;
  inspectorToggle: HTMLElement;
  timeline: HTMLElement;
  recordOverlay: HTMLElement;
  app: HTMLElement;
} {
  const app = document.getElementById('app')!;
  app.classList.add('app');

  const topbar = document.createElement('div');
  topbar.className = 'topbar';

  const statusbar = document.createElement('div');
  statusbar.className = 'statusbar';

  const layers = document.createElement('div');
  layers.className = 'layers';

  const splitL = document.createElement('div');
  splitL.className = 'split split-l';
  splitL.title = '좌측 패널 너비 조절';

  const stage = document.createElement('div');
  stage.className = 'stage';

  const splitR = document.createElement('div');
  splitR.className = 'split split-r';
  splitR.title = '우측 패널 너비 조절';

  const inspector = document.createElement('div');
  inspector.className = 'inspector';

  // Inspector toggle handle — sticks to the right edge when inspector is closed,
  // becomes a >> arrow inside the inspector header when open. Mobile too.
  const inspectorToggle = document.createElement('button');
  inspectorToggle.type = 'button';
  inspectorToggle.className = 'inspector-toggle';
  inspectorToggle.title = '인스펙터 닫기/열기';
  inspectorToggle.textContent = '»';
  inspectorToggle.addEventListener('click', () => {
    app.classList.toggle('inspector-collapsed');
    inspectorToggle.textContent = app.classList.contains('inspector-collapsed') ? '«' : '»';
  });

  const splitT = document.createElement('div');
  splitT.className = 'split split-t';
  splitT.title = '타임라인 높이 조절';

  const timeline = document.createElement('div');
  timeline.className = 'timeline';

  app.append(topbar, statusbar, layers, splitL, stage, splitR, inspector, inspectorToggle, splitT, timeline);

  installSplitter(app, splitL, '--side-w', 'left');
  installSplitter(app, splitR, '--inspector-w', 'right');
  installVerticalSplitter(app, splitT, '--timeline-h');

  // record overlay (in body, not in app grid)
  const recordOverlay = document.createElement('div');
  recordOverlay.className = 'record-overlay';
  document.body.appendChild(recordOverlay);

  return { topbar, statusbar, layers, stage, inspector, inspectorToggle, timeline, recordOverlay, app };
}

function installSplitter(
  app: HTMLElement,
  handle: HTMLElement,
  cssVar: string,
  side: 'left' | 'right',
): void {
  const STORAGE_KEY = `flash:layout:${cssVar}`;
  // The left column shrinks down to icon-only (52px); the right inspector
  // has its own >> toggle so it doesn't need to go that small.
  const min = cssVar === '--side-w' ? 52 : 200;
  const max = 720;

  // Apply to BOTH .app and :root so descendants outside the grid (e.g. the
  // popout panels mounted on <body>) can read the same variable.
  const apply = (px: number): void => {
    app.style.setProperty(cssVar, `${px}px`);
    document.documentElement.style.setProperty(cssVar, `${px}px`);
  };

  // Restore saved width
  try {
    const saved = parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10);
    if (Number.isFinite(saved) && saved >= min && saved <= max) {
      apply(saved);
    }
  } catch {
    // ignore
  }

  let drag: { startX: number; startWidth: number } | null = null;
  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    const cur = parseInt(getComputedStyle(app).getPropertyValue(cssVar), 10) || 240;
    drag = { startX: e.clientX, startWidth: cur };
    handle.classList.add('dragging');
    document.body.style.cursor = 'col-resize';
  });
  window.addEventListener('mousemove', (e) => {
    if (!drag) return;
    const delta = e.clientX - drag.startX;
    const next = side === 'left' ? drag.startWidth + delta : drag.startWidth - delta;
    const clamped = Math.max(min, Math.min(max, next));
    apply(clamped);
  });
  window.addEventListener('mouseup', () => {
    if (!drag) return;
    drag = null;
    handle.classList.remove('dragging');
    document.body.style.cursor = '';
    const v = parseInt(getComputedStyle(app).getPropertyValue(cssVar), 10);
    if (Number.isFinite(v)) {
      try {
        localStorage.setItem(STORAGE_KEY, String(v));
      } catch {
        // ignore
      }
    }
  });
}

/** Vertical sibling of installSplitter — drag the top edge of the timeline
 *  to grow / shrink it. Uses the same persistence pattern via localStorage. */
function installVerticalSplitter(
  app: HTMLElement,
  handle: HTMLElement,
  cssVar: string,
): void {
  const STORAGE_KEY = `flash:layout:${cssVar}`;
  const min = 120;
  const max = Math.max(280, Math.floor(window.innerHeight * 0.7));

  const apply = (px: number): void => {
    app.style.setProperty(cssVar, `${px}px`);
    document.documentElement.style.setProperty(cssVar, `${px}px`);
  };

  try {
    const saved = parseInt(localStorage.getItem(STORAGE_KEY) ?? '', 10);
    if (Number.isFinite(saved) && saved >= min && saved <= max) apply(saved);
  } catch {
    // ignore
  }

  let drag: { startY: number; startHeight: number } | null = null;
  handle.addEventListener('mousedown', (e) => {
    e.preventDefault();
    const cur = parseInt(getComputedStyle(app).getPropertyValue(cssVar), 10) || 200;
    drag = { startY: e.clientY, startHeight: cur };
    handle.classList.add('dragging');
    document.body.style.cursor = 'row-resize';
  });
  window.addEventListener('mousemove', (e) => {
    if (!drag) return;
    // Dragging UP grows the timeline; max recomputed every move to follow
    // window resizes mid-drag.
    const ceil = Math.max(280, Math.floor(window.innerHeight * 0.7));
    const next = drag.startHeight - (e.clientY - drag.startY);
    apply(Math.max(min, Math.min(ceil, next)));
  });
  window.addEventListener('mouseup', () => {
    if (!drag) return;
    drag = null;
    handle.classList.remove('dragging');
    document.body.style.cursor = '';
    const v = parseInt(getComputedStyle(app).getPropertyValue(cssVar), 10);
    if (Number.isFinite(v)) {
      try {
        localStorage.setItem(STORAGE_KEY, String(v));
      } catch {
        // ignore
      }
    }
  });
}

interface ViewerOpts {
  active: boolean;
  autoplay: boolean;
  loop: boolean;
  controls: boolean;
  clickToPlay: boolean;
  volume: number;
}

function readViewerOpts(): ViewerOpts {
  const url = new URL(window.location.href);
  return {
    active: url.searchParams.get('viewer') === '1',
    autoplay: url.searchParams.get('autoplay') === '1',
    loop: url.searchParams.get('loop') === '1',
    controls: url.searchParams.get('controls') !== '0',
    clickToPlay: url.searchParams.get('clickToPlay') !== '0',
    volume: parseFloat(url.searchParams.get('volume') ?? '1') || 1,
  };
}

async function main(): Promise<void> {
  // ─── Block native context menu first thing, before any UI is mounted.
  // Capture phase so we run before every surface-level handler. We still
  // allow the native menu inside form fields so the OS / IME niceties
  // (paste, dictation, suggestions) keep working there.
  window.addEventListener(
    'contextmenu',
    (e) => {
      const t = e.target as HTMLElement | null;
      if (t?.closest('input, textarea, [contenteditable="true"]')) return;
      e.preventDefault();
    },
    true,
  );

  const viewerOpts = readViewerOpts();
  if (viewerOpts.active) document.body.classList.add('viewer-mode');

  const { topbar, statusbar, layers, stage, inspector, timeline, recordOverlay } = buildShell();

  // Kick off async storage init (IndexedDB). UI mounts with the in-memory default
  // and re-renders when the saved project loads in. Once both promises resolve
  // the boot splash fades out — keep at least 600ms of splash so the animation
  // gets a moment to read (the editor is ABOUT animation, after all).
  const splashStart = performance.now();
  void Promise.all([initStore(), initFonts()]).finally(() => {
    const splash = document.getElementById('boot-splash');
    if (!splash) return;
    const elapsed = performance.now() - splashStart;
    const wait = Math.max(0, 700 - elapsed);
    setTimeout(() => {
      splash.classList.add('--gone');
      // Remove from DOM after the fade so the heavy animations stop ticking.
      setTimeout(() => splash.remove(), 700);
    }, wait);
  });

  const canvas = new Canvas(stage);
  new LayerPanel(layers);
  new Inspector(inspector);
  new Timeline(timeline);

  // Transport state
  let rafId: number | null = null;
  let lastTickAt = 0;

  function startPlayback(): void {
    const s = getState();
    if (s.isPlaying) return;
    setPlaying(true);
    if (s.playhead >= s.project.duration) setPlayhead(0);
    lastTickAt = performance.now();
    const tick = (now: number) => {
      const dt = now - lastTickAt;
      lastTickAt = now;
      const cur = getState();
      const next = cur.playhead + dt;
      if (next >= cur.project.duration) {
        setPlayhead(cur.project.duration);
        stopPlayback();
        return;
      }
      setPlayhead(next);
      rafId = requestAnimationFrame(tick);
    };
    rafId = requestAnimationFrame(tick);
  }
  function stopPlayback(): void {
    if (rafId != null) cancelAnimationFrame(rafId);
    rafId = null;
    setPlaying(false);
  }
  function togglePlay(): void {
    if (getState().isPlaying) stopPlayback();
    else startPlayback();
  }
  // (the reset-playhead button moved to the timeline toolbar)

  // Record mode
  function openRecordMode(): void {
    document.body.classList.add('record-mode');
    setRecordMode(true);
    buildRecordOverlay();
  }
  function closeRecordMode(): void {
    if (session.active) void session.stop();
    if (recTimer) {
      clearInterval(recTimer);
      recTimer = null;
    }
    document.body.classList.remove('record-mode', 'cap-region', 'cap-clip', 'cap-timeline');
    setRecordMode(false);
    recordOverlay.replaceChildren();
    canvas.showCountdown(null);
  }
  const session = new RecordingSession();
  let recBtn: HTMLButtonElement | null = null;
  let recStatus: HTMLElement | null = null;
  let recTimer: number | null = null;

  function updateRecStatus(): void {
    if (!recStatus) return;
    if (session.active) {
      const s = Math.round(session.elapsedMs / 1000);
      recStatus.textContent = `● REC ${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`;
      recStatus.style.color = '#ff5a5f';
    } else {
      recStatus.textContent = '';
    }
  }

  async function startRecording(): Promise<void> {
    try {
      const result = await session.start({
        fps: 30,
        cropElement: canvas.getFrameElement(),
      });
      if (recBtn) recBtn.textContent = '⏹ 캡쳐 정지';
      recTimer = window.setInterval(updateRecStatus, 500);
      updateRecStatus();
      if (recStatus) {
        recStatus.textContent = result.cropped
          ? '● REC (캔버스 영역만)'
          : '● REC (전체 — 영역 자르기 미지원 브라우저)';
      }
    } catch (err) {
      alert(`캡쳐 시작 실패: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  async function stopRecording(): Promise<void> {
    const result = await session.stop();
    if (recTimer) {
      clearInterval(recTimer);
      recTimer = null;
    }
    if (recBtn) recBtn.textContent = '🎥 화면 캡쳐 시작';
    updateRecStatus();
    if (result) {
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      downloadBlob(result.blob, `flash-${stamp}.${result.ext}`);
    }
  }

  async function takeScreenshot(): Promise<void> {
    try {
      const blob = await captureScreenshot();
      if (!blob) return;
      const stamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
      downloadBlob(blob, `flash-${stamp}.png`);
    } catch (err) {
      alert(`스크린샷 실패: ${err instanceof Error ? err.message : String(err)}`);
    }
  }

  function buildRecordOverlay(): void {
    recordOverlay.replaceChildren();
    const playBtn = document.createElement('button');
    playBtn.className = 'btn';
    playBtn.textContent = '▶ 3·2·1 후 재생';
    playBtn.addEventListener('click', () => playWithCountdown(playBtn));

    recBtn = document.createElement('button');
    recBtn.className = 'btn ghost';
    recBtn.textContent = session.active ? '⏹ 캡쳐 정지' : '🎥 화면 캡쳐 시작';
    recBtn.addEventListener('click', () => {
      if (session.active) void stopRecording();
      else void startRecording();
    });

    const shotBtn = document.createElement('button');
    shotBtn.className = 'btn ghost';
    shotBtn.textContent = '📸 스크린샷';
    shotBtn.addEventListener('click', () => void takeScreenshot());

    // Capture-region toggles. `cap-region` outlines the exact capture bounds
    // (a framing guide for external recorders); `cap-clip` masks anything that
    // spills past the canvas so the out-of-region overflow can be hidden.
    const mkToggle = (label: string, cls: string, title: string): HTMLButtonElement => {
      const b = document.createElement('button');
      b.title = title;
      const sync = (): void => {
        const on = document.body.classList.contains(cls);
        b.className = on ? 'btn primary' : 'btn ghost';
        b.textContent = `${on ? '☑' : '☐'} ${label}`;
      };
      b.addEventListener('click', () => {
        document.body.classList.toggle(cls);
        sync();
      });
      sync();
      return b;
    };
    const regionBtn = mkToggle('영역 표시', 'cap-region', '캡쳐 영역 테두리 표시/숨김');
    const clipBtn = mkToggle('영역 밖 가리기', 'cap-clip', '캔버스 밖으로 삐져나온 부분 가리기/보이기');
    const timelineBtn = mkToggle('타임라인', 'cap-timeline', '녹화 중 타임라인 표시 — 플레이헤드를 직접 옮기며 캡쳐');

    recStatus = document.createElement('div');
    recStatus.style.fontSize = '11px';
    recStatus.style.fontWeight = '700';
    recStatus.style.textAlign = 'center';
    updateRecStatus();

    const exitBtn = document.createElement('button');
    exitBtn.className = 'btn ghost';
    exitBtn.textContent = '✕ 나가기';
    exitBtn.addEventListener('click', closeRecordMode);
    recordOverlay.append(playBtn, recBtn, shotBtn, regionBtn, clipBtn, timelineBtn, recStatus, exitBtn);
  }
  function playWithCountdown(playBtn: HTMLButtonElement): void {
    playBtn.disabled = true;
    stopPlayback();
    setPlayhead(0);
    let n = 3;
    canvas.showCountdown(n);
    const tick = setInterval(() => {
      n -= 1;
      if (n > 0) {
        canvas.showCountdown(n);
      } else {
        clearInterval(tick);
        canvas.showCountdown(null);
        startPlayback();
        // re-enable play after clip ends + buffer
        const dur = getState().project.duration;
        setTimeout(() => {
          playBtn.disabled = false;
        }, dur + 1000);
      }
    }, 1000);
  }

  new TopBar(topbar, {
    onPlayToggle: togglePlay,
    onRecord: openRecordMode,
  });
  new StatusBar(statusbar);

  // ─── Background music (YouTube + file) ───
  // The toolbar audio button is gone; tracks are managed via "+ 사운드" in
  // the LayerPanel. Players still mount silently to keep playback alive.
  new YoutubePlayer();
  new HtmlAudioPlayer();

  // ─── Background popout (color / gradient / image / video) ───
  const bgPopout = mountPopoutPanel('🖼 배경', () => {
    const m = new BackgroundModal();
    m.open();
    return adoptModalContent(m);
  });

  // ─── Projects ───
  const projectModal = new ProjectModal();
  const projectsBtn = document.createElement('button');
  projectsBtn.className = 'btn ghost';
  projectsBtn.textContent = '📁 프로젝트';
  projectsBtn.title = '프로젝트 전환 / 새로 만들기 / 복제 / 삭제';
  projectsBtn.addEventListener('click', () => projectModal.open());
  topbar.appendChild(projectsBtn);

  // ─── Scripts + Fonts (popout panels next to the layer column) ───
  const scriptPopout = mountPopoutPanel('ⓢ 스크립트', () => {
    const m = new ScriptModal();
    m.open();
    // ScriptModal builds its own modal-bg; we re-host its inner content
    // into the popout body so it slides next to the layer column.
    return adoptModalContent(m);
  });
  const fontPopout = mountPopoutPanel('🅰 폰트', () => {
    const m = new FontModal((family) => {
      const { selectedId } = getProjectState();
      if (selectedId) updateProjectLayer(selectedId, { fontFamily: family } as Partial<unknown> as never);
    });
    m.open();
    return adoptModalContent(m);
  });
  setFontModalOpener(() => fontPopout.open());

  // Script + font buttons in their own section (titled "스크립트") at the bottom.
  const sideSection = document.createElement('div');
  sideSection.className = 'side-section';
  sideSection.dataset.sectionId = 'scripts';
  sideSection.draggable = true;
  const sideTitle = document.createElement('div');
  sideTitle.className = 'side-section-title';
  sideTitle.textContent = '스크립트';
  const sideBar = document.createElement('div');
  sideBar.className = 'layers-toolbar';

  const scriptBtn = document.createElement('button');
  scriptBtn.type = 'button';
  scriptBtn.className = 'add-icon';
  scriptBtn.title = '스크립트';
  scriptBtn.appendChild(iconHelper('code'));
  const sLab = document.createElement('span');
  sLab.className = 'add-icon-label';
  sLab.textContent = '스크립트';
  scriptBtn.appendChild(sLab);
  scriptBtn.addEventListener('click', () => scriptPopout.toggle());

  const fontBtn = document.createElement('button');
  fontBtn.type = 'button';
  fontBtn.className = 'add-icon';
  fontBtn.title = '폰트';
  fontBtn.appendChild(iconHelper('font'));
  const fLab = document.createElement('span');
  fLab.className = 'add-icon-label';
  fLab.textContent = '폰트';
  fontBtn.appendChild(fLab);
  fontBtn.addEventListener('click', () => fontPopout.toggle());

  sideBar.append(scriptBtn, fontBtn);
  sideSection.append(sideTitle, sideBar);
  layers.append(sideSection);

  // The + 배경 button lives in the LayerPanel add-row; expose the popout.
  (window as unknown as { _bgPopout?: typeof bgPopout })._bgPopout = bgPopout;

  ensureAllLoaded();

  // ─── AI assistant ───
  const assistant = new AssistantPanel(document.body);

  // Mount the AI button at the very top of the layer column inside its own
  // titled section, so it lines up with 컨트롤 / 오브젝트 / 스크립트.
  const aiSection = document.createElement('div');
  aiSection.className = 'side-section ai-section';
  aiSection.dataset.sectionId = 'agent';
  aiSection.draggable = true;

  const aiTitle = document.createElement('div');
  aiTitle.className = 'side-section-title';
  aiTitle.textContent = '에이전트';

  const aiBody = document.createElement('div');
  aiBody.className = 'ai-top-row';

  const aiBtn = document.createElement('button');
  aiBtn.type = 'button';
  aiBtn.className = 'add-icon ai-btn';
  aiBtn.title = 'AI 에이전트';
  aiBtn.appendChild(buildSparkleIcon());
  const aiLabel = document.createElement('span');
  aiLabel.className = 'add-icon-label';
  aiLabel.textContent = '어시스턴트';
  aiBtn.appendChild(aiLabel);
  aiBtn.addEventListener('click', () => assistant.toggle(true));
  aiBody.appendChild(aiBtn);

  aiSection.append(aiTitle, aiBody);
  layers.prepend(aiSection);

  // Now that ALL side sections are mounted (controls / objects / scripts /
  // agent), wire up drag-to-reorder so each one's drag listeners exist.
  enableSectionReorder(layers);

  // ─── External control surface (window API + postMessage + URL params) ───
  installExternalAPI();
  window.addEventListener('promo-studio:prompt', (e) => {
    const detail = (e as CustomEvent<string>).detail;
    if (typeof detail !== 'string' || !detail.trim()) return;
    assistant.runPrompt(detail);
  });

  // ─── About modal (data privacy notice) ───
  installAboutModal(topbar);

  // ─── Global CSS / JS escape hatches (per project) ───
  installGlobalCustomization();

  // ─── Esc cancels drawing tool, ⌘Z / ⌘⇧Z = undo/redo ───
  window.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && getProjectState().tool === 'draw') {
      setTool('select');
      return;
    }
    const target = e.target as HTMLElement | null;
    const inForm = !!target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName);

    const mod = e.metaKey || e.ctrlKey;
    if (mod && (e.key === 'z' || e.key === 'Z')) {
      if (inForm) return;
      e.preventDefault();
      if (e.shiftKey) projectRedo();
      else projectUndo();
      return;
    }

    // Layer shortcuts (only when a layer is selected and not editing a form)
    const { selectedId, editingId } = getProjectState();
    if (inForm || editingId) return;

    // Always block Backspace from navigating back, even with no selection.
    if (e.key === 'Backspace') e.preventDefault();

    if (!selectedId) return;

    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault(); // also blocks browser back-nav on Backspace
      const proj = getProjectState().project;
      const ids = getProjectState().selectedIds;
      const targetIds = ids.length > 0 ? ids : [selectedId];
      const names = targetIds.map((id) => proj.layers.find((l) => l.id === id)?.name ?? id);
      const head = names.slice(0, 3).join(', ');
      const more = targetIds.length > 3 ? ` 외 ${targetIds.length - 3}개` : '';
      const ok = window.confirm(`「${head}${more}」 항목을 삭제할까요? (총 ${targetIds.length}개)`);
      if (!ok) return;
      if (targetIds.length > 1) removeLayers(targetIds);
      else removeLayer(targetIds[0] as string);
      return;
    }
    if (mod && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault();
      duplicateLayer(selectedId);
      return;
    }
    if (mod && (e.key === 'c' || e.key === 'C')) {
      e.preventDefault();
      copyLayer(selectedId);
      return;
    }
    if (mod && (e.key === 'x' || e.key === 'X')) {
      e.preventDefault();
      cutLayer(selectedId);
      return;
    }
    if (mod && (e.key === 'v' || e.key === 'V')) {
      e.preventDefault();
      pasteLayer();
      return;
    }
    if (mod && e.key === ']') {
      e.preventDefault();
      if (e.shiftKey) bringToFront(selectedId);
      else bringForward(selectedId);
      return;
    }
    if (mod && e.key === '[') {
      e.preventDefault();
      if (e.shiftKey) sendToBack(selectedId);
      else sendBackward(selectedId);
      return;
    }
  });

  // ─── Viewer mode: optional autoplay / click-to-play / loop ───
  if (viewerOpts.active) {
    if (viewerOpts.clickToPlay) {
      stage.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.record-overlay, button, input, textarea, a')) return;
        togglePlay();
      });
    }
    if (viewerOpts.autoplay) {
      // Slight delay to let media + project finish loading from URL
      setTimeout(() => startPlayback(), 600);
    }
    if (viewerOpts.loop) {
      let lastPh = 0;
      subscribeProject((s) => {
        if (lastPh > 100 && s.playhead === 0 && !s.isPlaying) {
          // playback ended at duration → reset & restart
          setTimeout(() => startPlayback(), 200);
        }
        lastPh = s.playhead;
      });
    }
    // Hide controls if controls=0 was passed
    if (!viewerOpts.controls) document.body.classList.add('viewer-no-controls');
  }

  // Spacebar = play/pause when not focused on a text-entry element. Also bail
  // out if we're in inline-edit mode for a text layer — the textarea isn't
  // always focused yet at the very first space keystroke.
  window.addEventListener('keydown', (e) => {
    if (e.code === 'Space') {
      if (isTextEntryFocused(e.target)) return;
      if (getState().editingId) return;
      e.preventDefault();
      togglePlay();
    }
    if (e.code === 'Escape' && getState().recordMode) closeRecordMode();
  });

  // Programmatic toggle (used by context menus)
  window.addEventListener('promo-toggle-play', () => togglePlay());

  // External file drop is still wired up (drag an image / video from the
  // desktop to swap the background), but no on-screen hint — it conflicted
  // with the in-app layer drags ("뭐 어쩌란건지 ㅋㅋ"). Internal drags carry
  // text/plain payloads, file drags carry 'Files' in dataTransfer.types.
  window.addEventListener('dragover', (e) => {
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
  });
  window.addEventListener('drop', async (e) => {
    // Only swallow drops that carry an actual external file. Internal
    // text/plain drops (layer reorder, group assignment) must keep
    // bubbling up to the row-level drop handlers.
    if (!e.dataTransfer?.types?.includes('Files')) return;
    e.preventDefault();
    const file = e.dataTransfer?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const url = reader.result as string;
      const isVideo = file.type.startsWith('video/');
      updateProject((p) => ({
        ...p,
        canvas: {
          ...p.canvas,
          background: isVideo
            ? { kind: 'video', src: url, fit: 'cover', loop: true, muted: true }
            : { kind: 'image', src: url, fit: 'cover' },
        },
      }));
    };
    reader.readAsDataURL(file);
  });

  // Initial paint
  const s = getState();
  canvas.render(s.project, s.playhead, s.selectedId, s.recordMode);
  subscribe(() => {
    /* canvas already subscribes */
  });
}

/** True if the user is currently typing in an input/textarea/select or any
    contenteditable element — used to gate global keyboard shortcuts so we
    don't hijack typing. We check both the event target AND document.activeElement
    to be robust against weird cases (e.g. shadow roots, programmatic focus). */
function isTextEntryFocused(eventTarget: EventTarget | null): boolean {
  const targets: (Element | null)[] = [
    eventTarget instanceof Element ? eventTarget : null,
    document.activeElement,
  ];
  for (const el of targets) {
    if (!el) continue;
    const tag = el.tagName;
    if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
    if ((el as HTMLElement).isContentEditable) return true;
  }
  return false;
}

/** Sparkle / AI generative icon — 4-pointed stars at two scales. */
function buildSparkleIcon(): SVGSVGElement {
  const ns = 'http://www.w3.org/2000/svg';
  const svg = document.createElementNS(ns, 'svg');
  svg.setAttribute('viewBox', '0 0 16 16');
  svg.setAttribute('width', '18');
  svg.setAttribute('height', '18');
  svg.setAttribute('class', 'icon');
  // Big sparkle
  const big = document.createElementNS(ns, 'path');
  big.setAttribute(
    'd',
    'M9 2 L 10 6 L 14 7 L 10 8 L 9 12 L 8 8 L 4 7 L 8 6 Z',
  );
  big.setAttribute('fill', 'currentColor');
  // Small sparkle
  const small = document.createElementNS(ns, 'path');
  small.setAttribute('d', 'M4 10 L 4.5 11.5 L 6 12 L 4.5 12.5 L 4 14 L 3.5 12.5 L 2 12 L 3.5 11.5 Z');
  small.setAttribute('fill', 'currentColor');
  svg.append(big, small);
  return svg;
}

function installAboutModal(topbar: HTMLElement): void {
  const btn = document.createElement('button');
  // mobile-only — desktop already has the (?) privacy notice on the right
  btn.className = 'btn ghost about-btn';
  btn.textContent = 'ⓘ 정보';
  btn.title = '저장 위치 안내';
  topbar.appendChild(btn);

  const bg = document.createElement('div');
  bg.className = 'modal-bg';
  const modal = document.createElement('div');
  modal.className = 'modal';

  const h = document.createElement('h3');
  h.className = 'modal-title-with-logo';
  const logoImg = document.createElement('img');
  logoImg.src = '/logo.png';
  logoImg.alt = '';
  logoImg.className = 'modal-title-logo';
  const titleText = document.createElement('span');
  titleText.textContent = 'Float:Flash-html5 · 데이터 보관 안내';
  h.append(logoImg, titleText);

  const p1 = document.createElement('p');
  p1.textContent =
    '이 사이트는 정적 페이지(static)예요. 백엔드/계정/데이터베이스 없음. 우리한테 아무것도 안 와요.';

  const p2 = document.createElement('p');
  p2.textContent =
    '프로젝트(레이어, 시간, 텍스트 등)는 너의 브라우저 localStorage에만 저장돼요. 다른 브라우저나 기기에서는 안 보여요. 브라우저 데이터 지우면 같이 사라져요. 보존하려면 상단 "내보내기"로 JSON 받아두세요.';

  const p3 = document.createElement('p');
  p3.textContent =
    'AI 어시스턴트의 API 키는 너가 직접 입력하고, 우리 서버를 안 거쳐 OpenAI/Anthropic으로 곧장 가요. 기억하기 체크박스를 끄면 새로고침 시 키도 사라져요.';

  const p4 = document.createElement('p');
  p4.textContent =
    '오픈소스 (MIT). 직접 호스팅하거나 코드를 검사해도 돼요.';

  const linkRow = document.createElement('p');
  const link = document.createElement('a');
  link.href = 'https://github.com';
  link.target = '_blank';
  link.rel = 'noopener';
  link.textContent = 'GitHub 저장소 →';
  linkRow.appendChild(link);

  const credit = document.createElement('p');
  credit.style.marginTop = '8px';
  credit.style.color = 'var(--ink-mute)';
  credit.style.fontSize = '12px';
  credit.appendChild(document.createTextNode('만든곳: 플로트 주식회사 · '));
  const floatLink = document.createElement('a');
  floatLink.href = 'https://float.do';
  floatLink.target = '_blank';
  floatLink.rel = 'noopener';
  floatLink.textContent = 'https://float.do';
  credit.appendChild(floatLink);

  const claudeAside = document.createElement('p');
  claudeAside.style.marginTop = '4px';
  claudeAside.style.color = 'var(--ink-dim)';
  claudeAside.style.fontSize = '11px';
  claudeAside.style.fontStyle = 'italic';
  claudeAside.textContent =
    '("너"라고 하는 거 제가 한 거 아닙니다… Claude가 적은 거예요. 귀여워서 그냥 둘께요. — 대표·개발자 장청일)';

  const actions = document.createElement('div');
  actions.className = 'row-actions';
  const closeBtn = document.createElement('button');
  closeBtn.className = 'btn primary';
  closeBtn.textContent = '확인';
  closeBtn.addEventListener('click', () => bg.classList.remove('show'));
  actions.appendChild(closeBtn);

  modal.append(h, p1, p2, p3, p4, linkRow, credit, claudeAside, actions);
  bg.appendChild(modal);
  bg.addEventListener('click', (e) => {
    if (e.target === bg) bg.classList.remove('show');
  });
  document.body.appendChild(bg);

  btn.addEventListener('click', () => bg.classList.add('show'));
}

interface PopoutHandle {
  toggle: () => void;
  open: () => void;
  close: () => void;
}

/** Wraps a modal so that it slides out as a panel next to the layer column. */
function mountPopoutPanel(_label: string, opener: () => HTMLElement | null): PopoutHandle {
  let bg: HTMLElement | null = null;
  let isOpen = false;
  const handle: PopoutHandle = {
    open: () => {
      if (isOpen) return;
      bg = opener();
      if (!bg) return;
      bg.classList.add('as-popout');
      isOpen = true;
    },
    close: () => {
      if (!isOpen) return;
      bg?.classList.remove('show');
      bg = null;
      isOpen = false;
    },
    toggle: () => (isOpen ? handle.close() : handle.open()),
  };
  return handle;
}

/** Returns the .modal-bg element of a recently-opened modal. */
function adoptModalContent(_m: { open: () => void }): HTMLElement | null {
  // The modal appended its own .modal-bg.show as the last child of <body>
  const all = document.querySelectorAll('.modal-bg.show');
  return (all[all.length - 1] as HTMLElement) ?? null;
}

function installGlobalCustomization(): void {
  const styleEl = document.createElement('style');
  styleEl.id = 'project-global-css';
  document.head.appendChild(styleEl);

  type FnCompile = (...args: string[]) => (...args: unknown[]) => unknown;
  const dynCompile = (globalThis as { Function: unknown }).Function as unknown as FnCompile;

  let lastScript: string | null = null;
  let compiled: ((...a: unknown[]) => unknown) | null = null;

  subscribeProject((s) => {
    document.body.classList.toggle('draw-mode', s.tool === 'draw');
    const css = (s.project.scripts ?? []).length > 0
      ? concatScripts(s.project, 'css')
      : (s.project.globalCss ?? '');
    if (styleEl.textContent !== css) styleEl.textContent = css;

    const src = (s.project.scripts ?? []).length > 0
      ? concatScripts(s.project, 'js')
      : (s.project.globalScript ?? '');
    if (src !== lastScript) {
      lastScript = src;
      compiled = null;
      if (src) {
        try {
          compiled = dynCompile('t', 'project', 'callTool', src);
        } catch (err) {
          console.warn('global script compile error', err);
        }
      }
    }
    if (compiled) {
      try {
        const w = window as unknown as {
          promoStudio?: { callTool: (n: string, a: unknown) => unknown };
        };
        compiled(s.playhead, s.project, w.promoStudio?.callTool ?? (() => undefined));
      } catch (err) {
        console.warn('global script runtime error', err);
      }
    }
  });
}

main();
